/**
 * AnnualIncomeBreakdown.mask.ts
 *
 * Data-masking logic for the annual income breakdown tiles.
 * When showData is false, all visible value fields in IncomeTileConfig
 * are replaced with "--" so no real data is visible.
 */

import type { IncomeTileConfig } from "../../IncomeBreakdown/IncomeBreakdown.types";
import type { AWARD_PROGRESS } from "../../../../../common/enums/award-progress-codes";

const MASKED_VALUE = "--";

const maskTile = (tile: IncomeTileConfig): IncomeTileConfig => ({
  ...tile,
  header: tile.header ? { ...tile.header, value: MASKED_VALUE, tag: undefined } : undefined,
  linkRows: tile.linkRows
    ? { items: tile.linkRows.items.map((item) => ({ ...item, value: MASKED_VALUE })) }
    : undefined,
  metricCard: tile.metricCard
    ? { items: tile.metricCard.items.map((item) => ({ ...item, value: MASKED_VALUE })) }
    : undefined,
  legsProgress: tile.legsProgress
    ? { items: tile.legsProgress.items.map((item) => ({ ...item, value: MASKED_VALUE })) }
    : undefined,
  kpiTrackers: tile.kpiTrackers
    ? {
        items: tile.kpiTrackers.items.map((item) => ({
          ...item,
          percentage: 0,
          achievedSegments: 0,
          isMet: false,
          label: MASKED_VALUE,
        })),
      }
    : undefined,
  pvRequirement: tile.pvRequirement
    ? { ...tile.pvRequirement, percent: 0, met: false, label: MASKED_VALUE }
    : undefined,
  pvRequirements: tile.pvRequirements
    ? tile.pvRequirements.map((p) => ({ ...p, percent: 0, met: false, label: MASKED_VALUE }))
    : undefined,
  pinRequirement: tile.pinRequirement
    ? {
        ...tile.pinRequirement,
        value: MASKED_VALUE,
        met: false,
        status: "" as AWARD_PROGRESS,
        statusTag: undefined,
        label: MASKED_VALUE,
      }
    : undefined,
  pinRequirements: tile.pinRequirements
    ? tile.pinRequirements.map((p) => ({
        ...p,
        value: MASKED_VALUE,
        met: false,
        status: "" as AWARD_PROGRESS,
        statusTag: undefined,
        label: MASKED_VALUE,
      }))
    : undefined,
  statusBadge: undefined,
  stackedTiles: tile.stackedTiles
    ? tile.stackedTiles.map((st) => ({
        ...st,
        header: st.header ? { ...st.header, value: MASKED_VALUE } : undefined,
      }))
    : undefined,
  subTiles: tile.subTiles ? tile.subTiles.map(maskTile) : undefined,
});

export const maskAnnualIncomeTiles = (
  tiles: IncomeTileConfig[] | undefined,
  showData: boolean,
): IncomeTileConfig[] | undefined => {
  if (showData || !tiles) return tiles;
  return tiles.map(maskTile);
};
